import {
  IconAlphabetLatin,
  IconCalendar,
  IconCircle,
  IconCircle0Filled,
  IconCircleFilled,
  IconSignature,
} from '@tabler/icons-react'
import { FieldType } from 'database'
import { useTranslations } from 'i11n'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ScrollArea,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui'
import { usePDFStore } from '../../stores/pdf.store'
import { FieldItemIcon } from './FieldsEditLayer/FieldItem'
import { getColorHex } from '../../utils/getColorHex'
import { getFieldIsSignature, resortRecipients } from 'bff'
import {
  RecipientItem,
  RecipientItemLabel,
  RecipientItemMark,
  mapRecipientLabel,
} from '../Recipient'
import { getRecipientCanHaveFields } from 'bff/utils/get-recipient-can-have-fields'
import { localeFormatDate } from '../../utils/localeFormatDate'
import { useBlocks } from '../../hooks/useBlocks'

export const CanvasBlocks = ({ children }: { children?: React.ReactNode }) => {
  const recipients = usePDFStore((state) => state.recipients)
  const headerHeight = usePDFStore((state) => state.headerHeight)
  const currentRecipient = usePDFStore((state) =>
    state.recipients.find((r) => r.id === state.currentRecipientId)
  )!
  const currentRecipientId = usePDFStore((state) => state.currentRecipientId)
  const focusedBlock = usePDFStore((state) => state.focusedBlock)
  const update = usePDFStore((state) => state.update)
  const batchUpdate = usePDFStore((state) => state.batchUpdate)

  const t = useTranslations()

  const { canvasBlocks } = useBlocks()

  return (
    <ScrollArea
      style={{
        height: `calc(100vh - ${headerHeight})`,
      }}
      className='overflow-y-auto [&>div>div]:!block'
    >
      {children}
      <div className='font-medium space-y-5 py-4'>
        <div className='px-4'>
          <p>Destinatarios</p>
          <Select
            onValueChange={(value) => {
              batchUpdate({
                currentRecipientId: value,
                currentEditingFieldId: undefined,
              })
            }}
            defaultValue={currentRecipientId}
          >
            <SelectTrigger size='sm' className='bg-white'>
              <SelectValue placeholder='Selecciona un destinatario' />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {resortRecipients(recipients).map((recipient, index) => {
                  return (
                    <SelectItem key={recipient.id} value={recipient.id}>
                      <RecipientItem>
                        <RecipientItemMark color={recipient.color} />
                        <RecipientItemLabel>
                          {mapRecipientLabel(recipient, index, recipients)}
                        </RecipientItemLabel>
                      </RecipientItem>
                    </SelectItem>
                  )
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className='space-y-4'>
          <p className='px-4'>Campos</p>
          <div className='flex flex-col space-y-5'>
            {canvasBlocks.map((ele, index) => {
              return (
                <div key={index} className='flex flex-col space-y-2'>
                  <p className='px-4'>{ele.group}</p>
                  <div className='flex flex-col items-start'>
                    {ele.fields.map((field, index) => {
                      return (
                        <TooltipProvider
                          delayDuration={100}
                          key={`${ele.group}-${index}`}
                        >
                          <Tooltip>
                            <TooltipTrigger className='w-full'>
                              <Button
                                disabled={
                                  !getRecipientCanHaveFields(
                                    currentRecipient
                                  ) || field.disabled
                                }
                                onClick={() => {
                                  update(
                                    'focusedBlock',
                                    focusedBlock?.type === field.type
                                      ? undefined
                                      : field
                                  )
                                }}
                                className={cn(
                                  'w-full rounded-none text-left justify-start',
                                  {
                                    'bg-accent text-accent-foreground':
                                      focusedBlock?.type === field.type,
                                  }
                                )}
                                variant={'ghost'}
                              >
                                <span
                                  style={{
                                    background: getColorHex(
                                      currentRecipient!.color
                                    ),
                                  }}
                                  className='mr-2 p-[2px] rounded-md'
                                >
                                  <FieldItemIcon
                                    className='w-4 h-4 text-white'
                                    type={field.type}
                                  />
                                </span>
                                {field.label}
                              </Button>
                            </TooltipTrigger>
                            {field.disabled && (
                              <TooltipContent>
                                <p>{field.disabledReason}</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </ScrollArea>
  )
}
